import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Plus, Trash2, Copy, Check, LogOut, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

export default function Admin() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(1);
  const [label, setLabel] = useState("");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/license-keys");
      setKeys(data.keys || []);
    } catch {
      toast.error("Kon sleutels niet laden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/license-keys", { count: Number(count) || 1, label: label || null });
      toast.success(`${data.created.length} sleutel(s) aangemaakt`);
      setLabel("");
      load();
    } catch {
      toast.error("Genereren mislukt");
    } finally {
      setGenerating(false);
    }
  };

  const toggle = async (key, current) => {
    const next = current === "active" ? "available" : "active";
    setKeys((ks) => ks.map((k) => (k.key === key ? { ...k, status: next } : k)));
    try {
      await api.patch(`/admin/license-keys/${key}`, { status: next });
    } catch {
      toast.error("Wijzigen mislukt");
      load();
    }
  };

  const remove = async (key) => {
    setKeys((ks) => ks.filter((k) => k.key !== key));
    try {
      await api.delete(`/admin/license-keys/${key}`);
      toast.success("Sleutel verwijderd");
    } catch {
      toast.error("Verwijderen mislukt");
      load();
    }
  };

  const copy = (key) => {
    navigator.clipboard?.writeText(key);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const doLogout = () => { logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#0d1424] text-white px-4 py-8" data-testid="admin-screen">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-7 h-7 text-amber-400" />
            <h1 className="text-2xl font-bold">Licentiesleutels</h1>
          </div>
          <button
            data-testid="admin-logout"
            onClick={doLogout}
            className="flex items-center gap-2 text-sm text-white/60 hover:text-white px-3 py-2 rounded-xl border border-white/15"
          >
            <LogOut className="w-4 h-4" /> Uitloggen
          </button>
        </div>

        {/* generator */}
        <div className="bg-white/5 border border-white/15 rounded-2xl p-5 mb-8">
          <h2 className="text-sm font-semibold text-white/80 mb-3">Nieuwe sleutels genereren</h2>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-white/50 mb-1">Aantal</label>
              <input
                data-testid="admin-gen-count"
                type="number" min={1} max={100}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="w-24 px-3 py-2.5 rounded-xl bg-black/30 border border-white/15 text-white focus:outline-none focus:border-amber-400"
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs text-white/50 mb-1">Label (optioneel)</label>
              <input
                data-testid="admin-gen-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="bijv. Klant Jansen"
                className="w-full px-3 py-2.5 rounded-xl bg-black/30 border border-white/15 text-white placeholder-white/30 focus:outline-none focus:border-amber-400"
              />
            </div>
            <button
              data-testid="admin-gen-submit"
              onClick={generate}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-semibold disabled:opacity-60"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Genereren
            </button>
          </div>
        </div>

        {/* list */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-white/50" /></div>
        ) : keys.length === 0 ? (
          <p className="text-center text-white/40 py-16">Nog geen licentiesleutels. Genereer er hierboven een.</p>
        ) : (
          <div className="space-y-2" data-testid="admin-key-list">
            {keys.map((k) => (
              <div
                key={k.key}
                data-testid={`admin-key-row-${k.key}`}
                className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3"
              >
                <KeyRound className="w-4 h-4 text-white/40 shrink-0" />
                <span className="font-mono text-lg tracking-widest">{k.key}</span>
                {k.label && <span className="text-xs text-white/40 truncate">· {k.label}</span>}
                <div className="ml-auto flex items-center gap-2">
                  <button
                    data-testid={`admin-key-copy-${k.key}`}
                    onClick={() => copy(k.key)}
                    className="p-2 rounded-lg hover:bg-white/10 text-white/60"
                    title="Kopieer"
                  >
                    {copied === k.key ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <button
                    data-testid={`admin-key-toggle-${k.key}`}
                    onClick={() => toggle(k.key, k.status)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                      k.status === "active"
                        ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                        : "bg-white/10 text-white/50 border border-white/20"
                    }`}
                  >
                    {k.status === "active" ? "Actief" : "Beschikbaar"}
                  </button>
                  <button
                    data-testid={`admin-key-delete-${k.key}`}
                    onClick={() => remove(k.key)}
                    className="p-2 rounded-lg hover:bg-red-500/20 text-red-300"
                    title="Verwijder"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-6 text-xs text-white/40">
          <span className="text-emerald-300">Actief</span> = kan inloggen ·{" "}
          <span className="text-white/60">Beschikbaar</span> = geblokkeerd
        </p>
      </div>
    </div>
  );
}
